import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/http/client";
import type { Patient, PatientDraft, BatchResult } from "@/types/patient";
import type { PaginatedResponse } from "@/types/evaluation";

export type SchedulingQuery = { search?: string; page?: number; limit?: number };

export const patientKeys = {
  all: ["scheduling"] as const,
  list: (q: SchedulingQuery = {}) => [...patientKeys.all, "list", q] as const,
};

function toQueryString(q: SchedulingQuery): string {
  const p = new URLSearchParams();
  if (q.search) p.set("search", q.search);
  if (q.page)   p.set("page", String(q.page));
  if (q.limit)  p.set("limit", String(q.limit));
  const s = p.toString();
  return s ? "?" + s : "";
}

export function useScheduling(query: SchedulingQuery = {}) {
  return useQuery({
    queryKey: patientKeys.list(query),
    queryFn: () => api.get<PaginatedResponse<Patient>>(`/scheduling${toQueryString(query)}`),
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patient: PatientDraft) => api.post<Patient>("/scheduling", patient),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useCreatePatientsBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patients: PatientDraft[]) =>
      api.post<BatchResult>("/scheduling/batch", { patients }),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useDeletePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: true; id: number }>(`/scheduling/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useBatchDeletePatients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api.post<{ ok: true; deleted: number; notFound: number[] }>("/scheduling/batch-delete", { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientKeys.all }),
  });
}

export function useNotifyUpload() {
  return useMutation({
    mutationFn: (added: number) =>
      api.post<{ ok: true; sent: number; failed?: number; total: number }>(
        "/scheduling/notify-upload",
        { added }
      ),
  });
}
